import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle, Upload, Layers, Printer, Settings, DollarSign, Zap } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

export const HelpModal = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">How to Use</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-purple-600" />
            How to Use 3D Print Profit Calculator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Introduction */}
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-gray-700 leading-relaxed">
              This calculator helps you accurately determine the true cost of your 3D prints, including materials, electricity, machine depreciation, labor, and more. Use these costs to set profitable pricing for your prints.
            </p>
          </div>

          {/* Step 1 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Upload className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 1: Upload Your File</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Drag and drop or browse to upload your 3D model file. Supports <strong>STL</strong>, <strong>3MF</strong>, and <strong>G-code</strong> formats. The app will automatically analyze the model and extract dimensions.
            </p>
          </div>

          <Separator />

          {/* Step 2 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Layers className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 2: Select Material</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Choose your filament type (PLA, PETG, ABS, etc.). Each material has different density and cost per kg. You can also customize the material cost if needed by clicking the edit icon.
            </p>
            <div className="pl-12 text-xs text-gray-600 bg-gray-50 p-3 rounded border border-gray-200">
              <strong>💡 Tip:</strong> The calculator may auto-detect material from your filename if it follows common naming conventions.
            </div>
          </div>

          <Separator />

          {/* Step 3 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Settings className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 3: Configure Print Quality</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Select a quality preset (<strong>Draft</strong>, <strong>Standard</strong>, or <strong>High Quality</strong>). These presets adjust infill density, layer height, and other parameters that affect print time and material usage.
            </p>
            <ul className="list-disc list-inside pl-12 text-sm text-gray-700 space-y-1">
              <li><strong>Draft:</strong> Fast printing, 15% infill, 0.3mm layers</li>
              <li><strong>Standard:</strong> Balanced, 20% infill, 0.2mm layers</li>
              <li><strong>High Quality:</strong> Slow but detailed, 40% infill, 0.1mm layers</li>
            </ul>
          </div>

          <Separator />

          {/* Step 4 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Printer className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 4: Select Your Printer</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Choose your 3D printer model from the dropdown. Each printer has different power consumption, purchase cost, and lifespan that affect depreciation and operating costs.
            </p>
            <div className="pl-12 text-xs text-gray-600 bg-gray-50 p-3 rounded border border-gray-200">
              <strong>💡 Custom Printer:</strong> If your printer isn't listed, click <strong>+ Custom</strong> to enter your machine's specifications manually (cost, power consumption, lifetime hours, footprint).
            </div>
          </div>

          <Separator />

          {/* Step 5 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 5: Get Accurate Estimates (Optional)</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Click the green <strong>"Slice Now"</strong> button to get precise print time and material weight using real slicing calculations. This gives you the most accurate results based on your exact print settings.
            </p>
          </div>

          <Separator />

          {/* Step 6 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Settings className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 6: Configure Global Settings</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              Go to the <strong>Settings</strong> tab to configure:
            </p>
            <ul className="list-disc list-inside pl-12 text-sm text-gray-700 space-y-1">
              <li><strong>Electricity Rate:</strong> Your local cost per kWh (check your utility bill)</li>
              <li><strong>Annual Rent:</strong> Yearly cost of your workshop/facility space</li>
              <li><strong>Labour Rate:</strong> Your hourly cost including wages and overhead</li>
              <li><strong>Parts per Plate:</strong> How many copies you print at once</li>
            </ul>
          </div>

          <Separator />

          {/* Step 7 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Step 7: View Cost Breakdown</h3>
            </div>
            <p className="text-sm text-gray-700 pl-12">
              The calculator automatically computes your <strong>Cost of Goods Sold (COGS)</strong>, which includes:
            </p>
            <ul className="list-disc list-inside pl-12 text-sm text-gray-700 space-y-1">
              <li>Filament/Material cost</li>
              <li>Electricity cost based on print time and printer power</li>
              <li>Machine depreciation (purchase cost ÷ lifetime hours)</li>
              <li>Floor space cost (rent allocation based on printer footprint)</li>
              <li>Labour cost (pre-print tasks, post-processing)</li>
              <li>Optional components (if you add screws, inserts, etc.)</li>
            </ul>
            <div className="pl-12 text-xs text-gray-600 bg-green-50 p-3 rounded border border-green-200 mt-2">
              <strong>✨ Pro Tip:</strong> Add a markup (typically 2-4x COGS) to the per-part cost to set your selling price and ensure profitability!
            </div>
          </div>

          <Separator />

          {/* Hover Over Icons */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-blue-600" />
              Need More Help?
            </h4>
            <p className="text-sm text-gray-700">
              Look for the small <strong className="text-purple-600">?</strong> icons throughout the app. Hover over them to see detailed explanations of each field and why it matters for your cost calculations.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
